import { useApp } from '@/App';
import GenericErrorMessage from '@/components/GenericErrorMessage';
import { DocumentItem } from '@/components/modals/DocumentModal';
import { useModals } from '@/components/modals/ModalProvider';
import DataRow from '@/libs/antd/components/DataRow';
import FleetTitle from '@/libs/antd/components/Title';
import ViewManagementShareholder from '@/libs/antd/components/ViewManagementShareholder';
import { useForm } from '@/libs/antd/form';
import ANTD_DEFAULT_PROPS from '@/libs/antd/props';
import formRule from '@/libs/antd/rules';
import fetcher from '@/libs/fleet-api/fetcher';
import { useSelectSlikCheckingStatus } from '@/libs/fleet-api/hooks/master-data';
import {
	RequestSLIKCheckPayload,
	getProspectCustomerChecking,
	requestSLIKCheck,
} from '@/libs/fleet-api/services/prospect';
import { FileData } from '@/libs/fleet-api/services/types';
import { uploader } from '@/libs/fleet-api/uploader';
import {
	FileSearchOutlined,
	LoadingOutlined,
	UploadOutlined,
} from '@ant-design/icons';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
	Alert,
	Button,
	Col,
	DatePicker,
	Divider,
	Form,
	Input,
	Row,
	Select,
	Table,
	Upload,
	UploadFile,
} from 'antd';
import dayjs from 'dayjs';
import { FC, Fragment } from 'react';
import { masterDataToNumber } from '@/utils/masterdata-transform';

export type FRMCustomerCheckingFormProps = {
	prospectId: string;
};

const FRMCustomerCheckingForm: FC<FRMCustomerCheckingFormProps> = ({
	prospectId = '943P2024110005',
}) => {
	const { navigate, notification } = useApp();
	const {
		modal,
		openCustomerDetail,
		openCustomerGroupDetail,
		openDocumentModal,
		openProspectDetail,
		openNegativeHistoryModal,
		openRejectHistoryModal,
	} = useModals();
	const { data, isLoading, isFetching, isError } = useQuery({
		queryKey: ['getProspectCustomerChecking', prospectId] as const,
		queryFn: ({ queryKey }) => {
			const [, id] = queryKey;
			return getProspectCustomerChecking(id);
		},
	});

	const {
		mainInformation,
		customerNegativeHistory,
		customerRejectionHistory,
		customerCompanyManagementInformation,
		customerCompanyShareholderInformation,
		customerBusinessDocuments,
		customerLegalDocuments,
		customerReferralDocuments,
		approvalHistory,
		slikCheck,
		isRecheckSlik,
	} = data?.data || {};

	const customerType = mainInformation?.customerType?.id ?? 1;
	const isJF = customerType === 1;

	const { form, value, fieldName } = useForm<RequestSLIKCheckPayload>();

	const selectSlikCheckingStatus = useSelectSlikCheckingStatus();

	const mandatoryFiles =
		!isJF ||
		value?.slikCheckingStatus === 2 ||
		(value?.slikCheckingStatus === 1 &&
			(value?.slikCheckingDate === undefined ||
				dayjs().diff(value?.slikCheckingDate, 'day') > 90)) ||
		value?.slikCheckingStatus === undefined;

	const uploadSLIKMutation = useMutation({ mutationFn: uploader });
	const uploadApplicationLetterMutation = useMutation({ mutationFn: uploader });
	const checkSLIKMutation = useMutation({ mutationFn: requestSLIKCheck });

	// start stopper condition check
	const countShareholder = customerCompanyShareholderInformation?.length;
	const totalShareholding =
		countShareholder &&
		Number(
			customerCompanyShareholderInformation
				.reduce((acc, shareholder) => (acc += shareholder.shareholding), 0)
				.toFixed(2),
		);

	const isNegative = customerNegativeHistory?.some(
		(negative) =>
			negative.negativeStatus === 'Bad' && negative.isActive === 'Active',
	);

	const hasGroupBmpp = !!mainInformation?.customerGroup?.id;

	const isUploading =
		uploadSLIKMutation.isPending || uploadApplicationLetterMutation.isPending;

	const canSubmit =
		countShareholder && totalShareholding === 100 && !isNegative && hasGroupBmpp;
	// end stopper condition

	if (isError) return <GenericErrorMessage />;
	if (isFetching) {
		return (
			<LoadingOutlined
				style={{
					marginTop: 160,
					fontSize: 100,
					color: '#1557BC',
					display: 'block',
				}}
			/>
		);
	}

	return (
		<Fragment>
			<FleetTitle>Main Information</FleetTitle>
			<DataRow
				loading={isLoading}
				style={{
					marginBottom: 20,
				}}
				items={[
					[
						[
							'Nama Customer',
							<a
								href="#"
								className="trigger-popup"
								onClick={(e) => {
									e.preventDefault();
									openCustomerDetail(mainInformation!.customerId);
								}}
							>
								{mainInformation?.customerName}
							</a>,
						],
						[
							'Prospect ID',
							<a
								href="#"
								className="trigger-popup"
								onClick={(e) => {
									e.preventDefault();
									if (mainInformation?.prospectId) {
										openProspectDetail(mainInformation?.prospectId);
									}
								}}
							>
								{mainInformation?.prospectId}
							</a>,
						],
					],
					[
						['Kelas Industri', mainInformation?.industrySector.industryClass],
						['Sumber Order', mainInformation?.orderSource],
					],
					[
						[
							'Portofolio Guideline',
							mainInformation?.industrySector.portofolioGuideline,
						],
						[
							'Group Customer BMPP',
							mainInformation?.customerGroup?.id ? (
								<a
									href="#"
									className="trigger-popup"
									onClick={(e) => {
										e.preventDefault();
										openCustomerGroupDetail(mainInformation!.customerGroup.id);
									}}
									aria-disabled={!mainInformation?.customerGroup.id}
								>
									{mainInformation?.customerGroup.value ?? '-'}
								</a>
							) : (
								'-'
							),
						],
					],
					[
						[
							'Dokumen',
							<a
								href="#"
								className="trigger-popup"
								onClick={(e) => {
									e.preventDefault();

									const documents: DocumentItem[] = [];
									const getDocumentType = (name: string) => {
										if (name.endsWith('pdf')) return 'pdf';
										if (name.endsWith('jpeg')) return 'jpeg';
										if (name.endsWith('jpg')) return 'jpg';
										if (name.endsWith('png')) return 'png';
									};

									if (
										customerBusinessDocuments &&
										customerBusinessDocuments.length
									) {
										customerBusinessDocuments.forEach((document) => {
											documents.push({
												name: document.documentType,
												type: getDocumentType(document.file.name)!,
												url: document.file.url!,
											});
										});
									}

									if (customerLegalDocuments && customerLegalDocuments.length) {
										customerLegalDocuments.forEach((document) => {
											documents.push({
												name: document.documentType,
												type: getDocumentType(document.file.name)!,
												url: document.file.url!,
											});
										});
									}

									if (
										customerReferralDocuments &&
										customerReferralDocuments.length
									) {
										customerReferralDocuments.forEach((document) => {
											documents.push({
												name: document.documentType,
												type: getDocumentType(document.file.name)!,
												url: document.file.url!,
											});
										});
									}

									if (documents.length) {
										openDocumentModal(documents);
									} else {
										alert('no document found');
									}
								}}
							>
								View Dokumen
							</a>,
						],
					],
				]}
			/>

			<FleetTitle>Management/Shareholder Listing</FleetTitle>

			{(countShareholder === 0 || totalShareholding !== 100) && (
				<Alert
					message="Maaf, proses tidak dapat dilanjutkan karena data pemegang saham belum lengkap. Silakan lengkapi data tersebut untuk melanjutkan proses ini."
					type="warning"
					className="custom-alert"
					showIcon
					style={{ marginBottom: 20 }}
					action={
						<Button
							size="small"
							style={{ height: 'auto' }}
							onClick={() => {
								if (mainInformation?.customerId === '') return;
								navigate('CustomerPage', {
									customerId: mainInformation!.customerId,
								});
							}}
						>
							Update Customer
						</Button>
					}
				/>
			)}

			<ViewManagementShareholder
				managements={customerCompanyManagementInformation ?? []}
				shareholders={customerCompanyShareholderInformation ?? []}
				style={{ marginBottom: 20 }}
			/>

			<FleetTitle>Reject Customer History</FleetTitle>
			<Table
				style={{
					marginBottom: 20,
				}}
				loading={isLoading}
				pagination={false}
				dataSource={
					customerRejectionHistory?.map((reject, key) => {
						return {
							key,
							...reject,
						};
					}) ?? []
				}
				columns={[
					{
						title: 'No',
						render: (_, record) => record.no,
					},
					{
						title: 'Prospect ID',
						render: (_, record) => record.prospectId,
					},
					{
						title: 'Customer Name',
						render: (_, record) => record.customerName,
					},
					{
						title: 'Reject Status',
						align: 'center',
						render: (_, record) => record.rejectedStatus,
					},
					{
						title: 'Reject Date',
						align: 'center',
						render: (_, record) =>
							record.rejectedDate
								? dayjs(record.rejectedDate).format('DD/MM/YYYY')
								: '-',
					},
					{
						title: 'Rejected Process',
						align: 'center',
						render: (_, record) => record.rejectedProcess,
					},
					{
						title: 'Rejected By',
						align: 'center',
						render: (_, record) => record.rejectedBy,
					},
					{
						title: 'Detail Reject',
						align: 'center',
						render: (_, record) => (
							<a
								href="#"
								onClick={(e) => {
									e.preventDefault();
									openRejectHistoryModal(record.rejectedId);
								}}
							>
								<FileSearchOutlined />
							</a>
						),
					},
				]}
			/>

			<FleetTitle>Negative Customer History</FleetTitle>
			<Table
				style={{
					marginBottom: 20,
				}}
				scroll={{ x: 'max-content' }}
				loading={isLoading}
				pagination={false}
				dataSource={
					customerNegativeHistory?.map((negative, key) => {
						return {
							key,
							...negative,
						};
					}) ?? []
				}
				columns={[
					{
						title: 'No',
						render: (_, record) => record.no,
					},
					{
						title: 'Prospect ID',
						render: (_, record) => record.prospectId ?? '-',
					},
					{
						title: 'Customer Name',
						render: (_, record) => record.customerName,
					},
					{
						title: 'Negative Status',
						align: 'center',
						render: (_, record) =>
							record.negativeStatus
								? record.negativeStatus === 'Bad' ||
									record.negativeStatus === 'Warning'
									? 'Yes'
									: 'No'
								: '-',
					},
					{
						title: 'Type',
						align: 'center',
						render: (_, record) => (
							<span
								className={`negative-badge status-${record.negativeType?.toLowerCase()}`}
							>
								{record.negativeType}
							</span>
						),
					},
					{
						title: 'Negative Date',
						render: (_, record) =>
							record.negativeDate
								? dayjs(record.negativeDate).format('DD/MM/YYYY')
								: '-',
					},
					{
						title: 'Source',
						render: (_, record) => record.source,
					},
					{
						title: 'Active',
						align: 'center',
						render: (_, record) =>
							record.isActive === 'Active' ? 'Yes' : 'No',
					},
					{
						title: 'Detail Negative',
						align: 'center',
						render: (_, record) => (
							<a
								href="#"
								onClick={(e) => {
									e.preventDefault();
									openNegativeHistoryModal(record);
								}}
							>
								<FileSearchOutlined />
							</a>
						),
					},
				]}
			/>

			{isNegative && (
				<Alert
					message="Customer tidak dapat melanjutkan proses karena masuk dalam kategori BAD, silahkan melakukan open BAD di E-star"
					type="warning"
					className="custom-alert"
					showIcon
					style={{ marginBottom: 20 }}
				/>
			)}

			<FleetTitle>Request Checking SLIK</FleetTitle>
			<Form
				{...ANTD_DEFAULT_PROPS.FORM}
				form={form}
				validateTrigger={['onSubmit']}
				initialValues={
					{
						slikFormFile: slikCheck?.slikFormFile,
						applicationLetterFile: slikCheck?.applicationLetterFile,
						slikCheckingStatus: slikCheck?.slikCheckingStatus,
						slikCheckingDate: slikCheck?.slikCheckingDate,
					} as RequestSLIKCheckPayload
				}
				onFinish={async (values) => {
					const isConfirmed = await modal.confirm({
						title: 'Apakah Anda yakin dengan data yang Anda inputkan?',
						okText: 'YA',
						cancelText: 'TIDAK',
					});
					if (!isConfirmed) return;
					try {
						// APPROACH 1: Current approach (type assertion)
						// slikCheckingStatus: values.slikCheckingStatus?.id as number,

						// APPROACH 2: Utility function (recommended)
						// slikCheckingStatus: masterDataToNumber(values.slikCheckingStatus),

						// APPROACH 3: Utility with fallback (most robust)
						// slikCheckingStatus: masterDataToNumberWithFallback(values.slikCheckingStatus, 0),

						await checkSLIKMutation.mutateAsync({
							...values,
							prospectId: mainInformation!.prospectId,
							// Using APPROACH 2: Utility function (recommended)
							slikCheckingStatus: masterDataToNumber(values.slikCheckingStatus),
							slikFormFile: values.slikFormFile && {
								id: values.slikFormFile?.id as string,
								name: values.slikFormFile?.name as string,
								path: values.slikFormFile?.path as string,
							},
							applicationLetterFile: values.applicationLetterFile && {
								id: values.applicationLetterFile?.id as string,
								name: values.applicationLetterFile?.name as string,
								path: values.applicationLetterFile?.path as string,
							},
						});
						notification.success({
							message: 'Success',
							description: 'Submit Customer Checking Success',
						});
						navigate('FRMCustomerCheckingListPage');
					} catch (error) {
						if (fetcher.isError(error)) {
							const message = await error.json();
							notification.error({
								duration: 20,
								message: `Error ID: ${message?.metadata?.debugInfo?.errorId ?? 'Unknown'}`,
								description: (
									<div>
										{message?.metadata?.debugInfo?.messages &&
											message?.metadata?.debugInfo?.messages.length ? (
											<ul>
												{message?.metadata?.debugInfo?.messages.map(
													(msg: string) => <li>{msg}</li>,
												)}
											</ul>
										) : (
											<pre>Opps, Something went wrong</pre>
										)}
									</div>
								),
							});
						}
					}
				}}
			>
				{isJF && (
					<Fragment>
						{isRecheckSlik && (
							<Alert
								message="Terdapat request pengecekan SLIK ulang, silahkan upload ulang form FAPP dan PDP"
								type="warning"
								className="custom-alert"
								showIcon
								style={{ marginTop: 20 }}
							/>
						)}
						<Row
							gutter={[40, 20]}
							style={{ marginBottom: 20, marginTop: 20 }}
						>
							<Col span={12}>
								<Form.Item
									name={fieldName(['slikCheckingStatus'])}
									label="Status Pengecekan SLIK"
									rules={[formRule.required()]}
									labelCol={{
										span: 12,
										style: {
											alignSelf: 'center',
										},
									}}
									getValueFromEvent={(_, option) => option?.data}
									getValueProps={(value) => {
										if (!value) return {};
										return {
											value: {
												value: value.id,
												label: value.value,
											},
										};
									}}
								>
									<Select {...selectSlikCheckingStatus} />
								</Form.Item>
							</Col>
							<Col span={12}>
								{masterDataToNumber(value?.slikCheckingStatus) === 1 && (
									<Form.Item
										name={fieldName(['slikCheckingDate'])}
										label="Tanggal Pengecekan SLIK"
										rules={[formRule.required()]}
										labelCol={{
											span: 12,
											style: {
												alignSelf: 'center',
											},
										}}
										getValueProps={(value) => ({
											value: value && dayjs(value),
										})}
										normalize={(value) =>
											value && dayjs(value).utc(true).toISOString()
										}
									>
										<DatePicker
											maxDate={dayjs()}
											disabled={isRecheckSlik}
										/>
									</Form.Item>
								)}
							</Col>
						</Row>

						{value?.slikCheckingStatus === 1 &&
							dayjs().diff(value?.slikCheckingDate, 'day') > 90 && (
								<Alert
									message="Data SLIK debitur Anda sudah melewati masa berlaku, mohon untuk melakukan pembaruan data SLIK agar dapat melanjutkan proses."
									type="warning"
									className="custom-alert"
									showIcon
									style={{ marginTop: 20 }}
								/>
							)}
						<Divider />
					</Fragment>
				)}

				<Row gutter={[40, 20]}>
					<Col span={12}>
						<Form.Item
							name={fieldName(['slikFormFile'])}
							label="Upload Form FAPP"
							labelCol={{
								span: 12,
								style: {
									alignSelf: 'center',
								},
							}}
							getValueProps={(value) => {
								const file = value as FileData;
								return {
									fileList: file?.id
										? ([
											{
												uid: file.id,
												name: file.name,
												thumbUrl: file.url,
											},
										] as UploadFile[])
										: [],
								};
							}}
							normalize={({ file }: { file: UploadFile }) => {
								return {
									id: file.uid,
									format: file.type,
									name: file.name,
									url: file.thumbUrl,
								} as FileData;
							}}
							rules={mandatoryFiles ? [formRule.required()] : []}
						>
							<Upload.Dragger
								listType="picture"
								accept=".pdf, .doc, .docx"
								maxCount={1}
								beforeUpload={() => false}
								onRemove={() => {
									form.setFieldValue(fieldName(['slikFormFile']), null);
									return false;
								}}
								onChange={async ({ file }) => {
									const fileid = Date.now().toString();
									const filepath = `customer/${mainInformation!.customerId}/${prospectId}/form-fapp`;

									const url = await uploadSLIKMutation.mutateAsync({
										file: file as unknown as File,
										fileid,
										filepath,
									});

									form.setFieldValue(fieldName(['slikFormFile']), {
										format: 'jpeg',
										id: fileid,
										name: file.name,
										path: filepath,
										url,
									});
								}}
							>
								{uploadSLIKMutation.isPending ? (
									<LoadingOutlined
										style={{
											fontSize: 25,
											color: '#BABABA',
											display: 'block',
										}}
									/>
								) : (
									<Fragment>
										<UploadOutlined
											style={{
												fontSize: 25,
												color: '#BABABA',
												display: 'block',
											}}
										/>
										<div
											style={{
												fontSize: 12,
												marginTop: 10,
												color: '#BABABA',
											}}
										>
											Silakan Upload Form FAPP Anda
										</div>
									</Fragment>
								)}
							</Upload.Dragger>
						</Form.Item>
					</Col>

					<Col span={12}>
						<Form.Item
							name={fieldName(['applicationLetterFile'])}
							label="Upload Form PDP"
							labelCol={{
								span: 12,
								style: {
									alignSelf: 'center',
								},
							}}
							getValueProps={(value) => {
								const file = value as FileData;
								return {
									fileList: file?.id
										? ([
											{
												uid: file.id,
												name: file.name,
												thumbUrl: file.url,
											},
										] as UploadFile[])
										: [],
								};
							}}
							normalize={({ file }: { file: UploadFile }) => {
								return {
									id: file.uid,
									format: file.type,
									name: file.name,
									url: file.thumbUrl,
								} as FileData;
							}}
							rules={mandatoryFiles ? [formRule.required()] : []}
						>
							<Upload.Dragger
								listType="picture"
								accept=".pdf, .doc, .docx"
								maxCount={1}
								beforeUpload={() => false}
								onRemove={() => {
									form.setFieldValue(
										fieldName(['applicationLetterFile']),
										null,
									);
									return false;
								}}
								onChange={async ({ file }) => {
									const fileid = Date.now().toString();
									const filepath = `customer/${mainInformation!.customerId}/${prospectId}/form-pdp`;

									const url = await uploadApplicationLetterMutation.mutateAsync(
										{
											file: file as unknown as File,
											fileid,
											filepath,
										},
									);

									form.setFieldValue(fieldName(['applicationLetterFile']), {
										format: 'jpeg',
										id: fileid,
										name: file.name,
										path: filepath,
										url,
									});
								}}
							>
								{uploadApplicationLetterMutation.isPending ? (
									<LoadingOutlined
										style={{
											fontSize: 25,
											color: '#BABABA',
											display: 'block',
										}}
									/>
								) : (
									<Fragment>
										<UploadOutlined
											style={{
												fontSize: 25,
												color: '#BABABA',
												display: 'block',
											}}
										/>
										<div
											style={{
												fontSize: 12,
												marginTop: 10,
												color: '#BABABA',
											}}
										>
											Silakan Upload Form PDP Anda
										</div>
									</Fragment>
								)}
							</Upload.Dragger>
						</Form.Item>
					</Col>
				</Row>
				{approvalHistory && approvalHistory.length ? (
					<Fragment>
						<Divider />
						<Table
							bordered
							loading={isLoading}
							pagination={false}
							dataSource={
								data?.data.approvalHistory?.map((approval, key) => {
									return {
										key,
										...approval,
									};
								}) ?? []
							}
							columns={[
								{
									title: 'No',
									render: (_, record) => record.no,
								},
								{
									title: 'Name',
									render: (_, record) => record.approvalBy,
								},
								{
									title: 'Date',
									render: (_, record) => record.approvalDate,
								},
								{
									title: 'Process',
									render: (_, record) => record.approvalProcess,
								},
								{
									title: 'Status',
									align: 'center',
									render: (_, record) => {
										return (
											<span
												className={`review-status ${record.approvalStatus.toLowerCase()}`}
											>
												{record.approvalStatus}
											</span>
										);
									},
								},
								{
									title: 'Approval Notes',
									render: (_, record) => record.approvalNote,
								},
							]}
							style={{ marginBottom: 20 }}
						/>
					</Fragment>
				) : null}
				<Divider />
				<Row gutter={[40, 20]}>
					<Col span={24}>
						<Form.Item
							name={fieldName(['additionalInformation'])}
							label="Informasi Tambahan"
							labelCol={{
								span: 6,
								style: {
									alignSelf: 'center',
								},
							}}
							wrapperCol={{
								style: {
									marginLeft: -10,
								},
							}}
							rules={[formRule.required()]}
						>
							<Input.TextArea rows={4} />
						</Form.Item>
					</Col>
				</Row>
				{!hasGroupBmpp && (
					<Alert
						message={<span style={{ color: '#ff0000' }}>Maaf, proses tidak dapat dilanjutkan karena data Group BMPP belum lengkap. Silakan lengkapi data tersebut untuk melanjutkan proses ini.</span>}
						type="error"
						className="custom-alert"
						showIcon
						style={{ marginTop: 40 }}
					/>
				)}
				<Button
					type="primary"
					htmlType="submit"
					disabled={!canSubmit || isUploading}
					loading={checkSLIKMutation.isPending}
					style={{
						width: 200,
						display: 'block',
						marginLeft: 'auto',
						marginTop: 40,
					}}
				>
					SAVE
				</Button>

			</Form>
		</Fragment>
	);
};

export default FRMCustomerCheckingForm;
